/** Local session cache for effective permissions (no API imports — avoids cycles with AxiosHeaders). */

import {
  buildSuperAdminOnlyPermissionFlags,
  isPermissionsSuperAdmin,
} from "./superAdmin";
import { normalizePermissionGroupIds } from "./permissionGroupMembership";

export const PERMISSIONS_STORAGE_KEY = "permissions";
export const PERMISSION_GROUP_ID_KEY = "permission_group_id";
export const PERMISSION_GROUP_IDS_KEY = "permission_group_ids";
export const PERMISSION_GROUP_NAME_KEY = "permission_group_name";
export const PERMISSION_GROUPS_KEY = "permission_groups";
export const PERMISSIONS_SOURCE_KEY = "permissions_source";
export const PERMISSIONS_UPDATED_EVENT = "cms:permissions-updated";
/** Fire to force a server refresh (e.g. after admin changes group membership). */
export const PERMISSIONS_REFRESH_REQUEST_EVENT = "cms:permissions-refresh-requested";

export function isPermissionEnabled(value) {
  return value === 1 || value === "1" || value === true;
}

function readJson(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    if (raw == null || raw === "") return fallback;
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

function persistPermissionGroupMeta(meta) {
  const ids = Array.isArray(meta.permission_group_ids)
    ? meta.permission_group_ids.map((id) => Number(id)).filter((id) => id > 0)
    : [];

  if (ids.length) {
    localStorage.setItem(PERMISSION_GROUP_IDS_KEY, JSON.stringify(ids));
    localStorage.setItem(PERMISSION_GROUP_ID_KEY, String(ids[0]));
  } else {
    localStorage.removeItem(PERMISSION_GROUP_IDS_KEY);
    localStorage.removeItem(PERMISSION_GROUP_ID_KEY);
    localStorage.removeItem(PERMISSION_GROUP_NAME_KEY);
    localStorage.removeItem(PERMISSION_GROUPS_KEY);
  }

  if (meta.permission_group_name) {
    localStorage.setItem(
      PERMISSION_GROUP_NAME_KEY,
      String(meta.permission_group_name),
    );
  } else if (!ids.length) {
    localStorage.removeItem(PERMISSION_GROUP_NAME_KEY);
  }

  if (Array.isArray(meta.permission_groups) && meta.permission_groups.length) {
    localStorage.setItem(
      PERMISSION_GROUPS_KEY,
      JSON.stringify(meta.permission_groups),
    );
  } else if (!ids.length) {
    localStorage.removeItem(PERMISSION_GROUPS_KEY);
  }
}

export function normalizePermissionsPayload(payload) {
  if (!payload || typeof payload !== "object") {
    return { flags: {}, meta: {} };
  }

  const hasNested =
    payload.permissions &&
    typeof payload.permissions === "object" &&
    !Array.isArray(payload.permissions);

  if (hasNested) {
    const ids =
      Array.isArray(payload.permission_group_ids) &&
      payload.permission_group_ids.length
        ? payload.permission_group_ids
        : payload.permission_group_id != null &&
            payload.permission_group_id !== "" &&
            Number(payload.permission_group_id) !== 0
          ? [payload.permission_group_id]
          : [];

    return {
      flags: payload.permissions,
      meta: {
        permission_group_id: ids[0] ?? payload.permission_group_id ?? null,
        permission_group_ids: ids.map((id) => Number(id)),
        permission_group_name: payload.permission_group_name ?? null,
        permission_groups: payload.permission_groups ?? [],
        source: payload.source ?? null,
      },
    };
  }

  return { flags: payload, meta: {} };
}

function syncPermissionGroupMetaFromUser() {
  const user = readJson("user", null);
  const ids = normalizePermissionGroupIds(user);
  if (ids.length) {
    persistPermissionGroupMeta({
      permission_group_ids: ids,
      permission_group_id: ids[0],
      permission_group_name: user?.permission_group_name ?? null,
      permission_groups: user?.permission_groups ?? [],
    });
  }
}

export function hasAssignedPermissionGroup() {
  const ids = readJson(PERMISSION_GROUP_IDS_KEY, null);
  if (Array.isArray(ids) && ids.length > 0) {
    return true;
  }
  const idRaw = localStorage.getItem(PERMISSION_GROUP_ID_KEY);
  if (idRaw != null && idRaw !== "" && Number(idRaw) !== 0) {
    return true;
  }
  const user = readJson("user", null);
  return normalizePermissionGroupIds(user).length > 0;
}

export function applyUserPermissionsPayload(payload) {
  const { flags, meta } = normalizePermissionsPayload(payload);

  persistPermissionGroupMeta(meta);

  if (meta.source) {
    localStorage.setItem(PERMISSIONS_SOURCE_KEY, String(meta.source));
  } else {
    localStorage.removeItem(PERMISSIONS_SOURCE_KEY);
  }

  syncPermissionGroupMetaFromUser();

  let effectiveFlags = flags || {};
  if (!hasAssignedPermissionGroup()) {
    effectiveFlags = isPermissionsSuperAdmin()
      ? buildSuperAdminOnlyPermissionFlags()
      : {};
  }

  localStorage.setItem(
    PERMISSIONS_STORAGE_KEY,
    JSON.stringify(effectiveFlags),
  );

  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent(PERMISSIONS_UPDATED_EVENT, {
        detail: {
          permissions: effectiveFlags,
          meta: getPermissionGroupMeta(),
        },
      }),
    );
  }

  return effectiveFlags;
}

export function getPermissions() {
  const parsed = readJson(PERMISSIONS_STORAGE_KEY, {});
  return parsed && typeof parsed === "object" && !Array.isArray(parsed)
    ? parsed
    : {};
}

export function hasPermission(key) {
  if (!key) return false;
  if (isPermissionsSuperAdmin() && key === "permissions_page") {
    return true;
  }
  if (!hasAssignedPermissionGroup()) return false;
  return isPermissionEnabled(getPermissions()[key]);
}

export function getPermissionGroupMeta() {
  const ids = readJson(PERMISSION_GROUP_IDS_KEY, []);
  const normalizedIds = Array.isArray(ids)
    ? ids.map((id) => Number(id)).filter((id) => id > 0)
    : [];
  const idRaw = localStorage.getItem(PERMISSION_GROUP_ID_KEY);
  const fallbackId =
    idRaw != null && idRaw !== "" ? Number(idRaw) : null;

  return {
    permission_group_id: normalizedIds[0] ?? fallbackId,
    permission_group_ids: normalizedIds.length
      ? normalizedIds
      : fallbackId
        ? [fallbackId]
        : [],
    permission_group_name:
      localStorage.getItem(PERMISSION_GROUP_NAME_KEY) || null,
    permission_groups: readJson(PERMISSION_GROUPS_KEY, []),
    source: localStorage.getItem(PERMISSIONS_SOURCE_KEY) || null,
  };
}

export function getLoggedInUserId() {
  const user = readJson("user", null);
  if (user?.id != null) return user.id;
  const id = readJson("user_id", null);
  return id != null ? id : null;
}

export function currentUserBelongsToPermissionGroup(groupId) {
  if (groupId == null || groupId === "") return false;
  const target = Number(groupId);
  const metaIds = getPermissionGroupMeta().permission_group_ids || [];
  if (metaIds.includes(target)) return true;
  const user = readJson("user", null);
  return normalizePermissionGroupIds(user).includes(target);
}
